import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useRef } from 'react';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { operationsSearch } from '@reducers/search';
import { setJobId, setJobs, setProductionOrderJobsList } from '@reducers/details';
import { ProductionOrderContentWrapperProps } from '@v2/pages/productionOrder/modules/productionOrder/components/ProductionOrderContentWrapper';

export interface UseManageProductionOrderContentWrapperParams
  extends Pick<
    ProductionOrderContentWrapperProps,
    'productionOrder' | 'setJobsApiCall' | 'setIsError' | 'setNoData' | 'setTotalCount'
  > {}

export const useManageProductionOrderContentWrapper = ({
  productionOrder,
  setJobsApiCall,
  setIsError,
  setNoData,
  setTotalCount,
}: UseManageProductionOrderContentWrapperParams) => {
  const dispatch = useAppDispatch();
  const productionOrderId = productionOrder?.productionOrderId;

  const jobsAbortController = useRef<AbortController | null>(null);

  const jobsApiCall = () => {
    if (!jobsAbortController.current) {
      jobsAbortController.current = new AbortController();
    }

    return dispatch(
      operationsSearch({
        data: { orderBy: ['SortOrder'], search: '*' },
        filter: { oDataFilter: `ProductionOrderId eq ${productionOrderId}` },
        select: [],
        resetHistory: true,
        ignoreHistory: true,
        signal: jobsAbortController.current.signal,
      }),
    );
  };

  useFocusEffect(
    useCallback(() => {
      setJobsApiCall?.(jobsApiCall);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  useFocusEffect(
    useCallback(() => {
      jobsAbortController.current = new AbortController();

      setIsError?.(false);

      if (productionOrderId == null) return;

      (async () => {
        try {
          const result = await jobsApiCall();

          if (jobsAbortController.current?.signal.aborted) return;

          setNoData?.(!result?.searchResult || result?.searchResult?.length === 0);

          if (result?.searchResult) {
            setTotalCount?.(result?.totalResultCount);
            dispatch(setJobs(result?.searchResult));
            dispatch(setProductionOrderJobsList(result?.searchResult));
            if (result?.searchResult?.length === 1) {
              dispatch(setJobId(result?.searchResult?.[0]?.productionOperationId));
            }
          }
        } catch (e) {
          if (e instanceof Error && e.name === 'AbortError') {
            return;
          }
          console.error(e);
          setIsError?.(true);
        }
      })();

      return () => {
        jobsAbortController.current?.abort();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productionOrderId]),
  );
};
